<template>
  <!--
    微信浮窗引导（蒙版弹窗版）
    - 仅微信内展示：关键动作（盲盒报名成功 / AI红娘预约确认成功）后、或登录用户进入盲盒CP页时触发
    - 展示时全屏黑色半透明蒙版突出卡片；点蒙版空白处 / 右上角 × 均关闭
    - 点 × 或蒙版仅本次关闭；勾选「不再提示」后再关闭 → 落本地记录永久不再提示
  -->
  <Transition name="float-guide-fade">
    <div
      v-if="state.visible"
      class="float-guide-mask"
      @click.self="closeFloatGuide(dontRemind)"
    >
      <div
        class="float-guide-card"
        role="dialog"
        aria-modal="true"
        aria-label="微信浮窗引导"
      >
        <!-- 卡片右上方小三角：指向微信右上角「···」 -->
        <span class="float-guide-arrow" aria-hidden="true"></span>

        <button
          type="button"
          class="float-guide-close"
          aria-label="关闭浮窗引导"
          @click.stop="closeFloatGuide(dontRemind)"
        >
          <Icon icon="solar:close-circle-bold" :size="24" />
        </button>

        <p class="guide-line guide-line--title">下次想快速找到我？</p>

        <!-- 方式一：浮窗 -->
        <div class="guide-option">
          <p class="guide-option__main">
            点右上角<span class="guide-hl">「···」</span>
            <Icon class="guide-arrow-icon" icon="solar:arrow-right-linear" :size="18" />
            再点<span class="guide-hl">「浮窗」</span>
          </p>
          <p class="guide-line guide-line--sub">微信首页下拉 / 侧边栏就能一键回来</p>
        </div>

        <!-- 方式二：星标 -->
        <div class="guide-option">
          <p class="guide-option__main">
            点右上角<span class="guide-hl">「···」</span>
            <Icon class="guide-arrow-icon" icon="solar:arrow-right-linear" :size="18" />
            再点<span class="guide-hl">「星标」</span>
          </p>
          <p class="guide-line guide-line--sub">从「收藏」中即可快速访问</p>
        </div>

        <label class="guide-opt" @click.stop>
          <input v-model="dontRemind" type="checkbox" class="guide-opt__input" />
          <span class="guide-opt__box" aria-hidden="true">
            <Icon v-if="dontRemind" icon="solar:check-bold" :size="14" />
          </span>
          <span class="guide-opt__label">不再提示</span>
        </label>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { Icon } from '@iconify/vue'
import { useFloatGuide } from '@/composables/useFloatGuide'

const { state, closeFloatGuide } = useFloatGuide()

/** 「不再提示」勾选态：每次弹窗展示时重置为未勾选 */
const dontRemind = ref(false)
watch(
  () => state.visible,
  (v) => {
    if (v) dontRemind.value = false
  }
)
</script>

<style lang="scss" scoped>
/* 全屏黑色半透明蒙版：展示期间拦截下方页面操作 */
.float-guide-mask {
  position: fixed;
  inset: 0;
  z-index: 99; // 全局遮罩层：高于普通内容/悬浮卡片/内部弹窗，低于业务系统弹窗(2000+)
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.55);
}

/* 引导卡片：贴近视口上方（指向微信右上角 ···），米白金边强对比，老钱风 */
.float-guide-card {
  position: absolute;
  top: calc(env(safe-area-inset-top, 0px) + 76px);
  left: 20px;
  right: 20px;
  box-sizing: border-box;
  padding: 22px 20px 18px;
  border-radius: 16px;
  background: linear-gradient(160deg, #fffdf7 0%, #f8f0e0 100%);
  border: 1.5px solid #b89b6a;
  /* 禁用移动端文本自动放大，保证引导文案按设计尺寸渲染，避免卡片超高溢出 */
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
  box-shadow:
    0 16px 44px rgba(0, 0, 0, 0.38),
    0 2px 8px rgba(92, 64, 51, 0.18);
}

/* 指向右上角的三角（与卡片同底同边） */
.float-guide-arrow {
  position: absolute;
  top: -9px;
  right: 40px;
  width: 16px;
  height: 16px;
  background: #fffdf7;
  border-top: 1.5px solid #b89b6a;
  border-left: 1.5px solid #b89b6a;
  transform: rotate(45deg);
  border-top-left-radius: 3px;
}

.float-guide-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 36px;
  height: 36px;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: #5c4033;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:active {
    opacity: 0.65;
  }
}

.guide-line {
  margin: 0;
  text-align: center;
  color: #5c4033;
}

.guide-line--title {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.45;
  letter-spacing: 0.5px;
}

.guide-line--main {
  margin-top: 12px;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
}

/* 两种快速找回方式：浮窗 / 星标 */
.guide-option {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed rgba(184, 155, 106, 0.35);

  &:first-of-type {
    margin-top: 12px;
    padding-top: 0;
    border-top: none;
  }
}

.guide-option__main {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  color: #5c4033;
}

.guide-arrow-icon {
  color: #a07f3e;
  margin: 0 2px;
  flex-shrink: 0;
}

/* 关键操作词：深金色加粗，米白底上高对比 */
.guide-hl {
  color: #8a6a2c;
  font-weight: 700;
}

.guide-line--sub {
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #75624f;
}

/* 「不再提示」勾选行：放大点击区与字号，深棕文字清晰 */
.guide-opt {
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;

  &__input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    margin: 0;
  }

  &__box {
    width: 18px;
    height: 18px;
    border-radius: 5px;
    border: 1.5px solid #b89b6a;
    background: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    box-sizing: border-box;
    flex-shrink: 0;
    transition: background 0.15s ease, border-color 0.15s ease;
  }

  &__input:checked + &__box {
    background: #b89b6a;
    border-color: #b89b6a;
  }

  &__label {
    font-size: 14px;
    line-height: 1;
    color: #5c4033;
    font-weight: 500;
    letter-spacing: 0.3px;
  }
}

/* 进入/离开：蒙版淡入淡出 + 卡片自上方轻微下落 */
.float-guide-fade-enter-active,
.float-guide-fade-leave-active {
  transition: opacity 220ms ease;

  .float-guide-card {
    transition: transform 220ms ease, opacity 220ms ease;
  }
}
.float-guide-fade-enter-from,
.float-guide-fade-leave-to {
  opacity: 0;

  .float-guide-card {
    opacity: 0;
    transform: translateY(-14px) scale(0.98);
  }
}
</style>
